import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { OrganizationActivity } from '../data-models/organization-activity.model';
import { OrganizationCustomer } from '../response-models/organization-customer.model';
import { OrganizationCustomerField } from '../response-models/organization-customer-field.model';
import { OrganizationTestResult } from '../response-models/organization-test-result.model';
import { Page } from '../response-models/page.model';
import { ApiListResponse } from '../response-models/api-list-response.model';
import { isRecord } from '../utils/is-record';
import { singlePage } from '../utils/paging-utils';
import { textOrNull } from '../utils/text-wire-utils';
import { countOrZero, idString, nullableNumber } from '../utils/wire-value-utils';

/**
 * Read-only organization data for the admin org-detail tabs: customers, a customer's fields and safety test
 * results (hub `admin-organization-data-controller.ts`) and the activity feed (`admin-activities-controller.ts`).
 * These are flattened admin views, so this is an api-service. Customers and activities are paged with `limit`.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationDataService extends ApiService {
  constructor() {
    super('admin/organizations');
  }

  /** GET /admin/organizations/:id/customers?page&limit&search */
  getCustomers(organizationId: string, page: number, limit: number, search?: string): Observable<Page<OrganizationCustomer>> {
    const query = { page, limit, search: search?.trim() || undefined };
    return this.requestEnvelope('GET', { id: organizationId, path: 'customers', query }, body => toPage(body, toCustomer));
  }

  /** GET /admin/organizations/:id/customers/:customerId/fields */
  getCustomerFields(organizationId: string, customerId: string): Observable<OrganizationCustomerField[]> {
    const opts = { id: organizationId, path: ['customers', customerId, 'fields'] };
    return this.request('GET', opts, data => (Array.isArray(data) ? data.map(toField) : []));
  }

  /** GET /admin/organizations/:id/test-results: each member's latest completed attempt per test. */
  getTestResults(organizationId: string): Observable<OrganizationTestResult[]> {
    return this.request('GET', { id: organizationId, path: 'test-results' }, data => (Array.isArray(data) ? data.map(toTestResult) : []));
  }

  /** GET /admin/organizations/:id/activities?page&limit, newest first. */
  getActivities(organizationId: string, page: number, limit: number): Observable<Page<OrganizationActivity>> {
    const opts = { id: organizationId, path: 'activities', query: { page, limit } };
    return this.requestEnvelope('GET', opts, body => toPage(body, item => new OrganizationActivity(item)));
  }
}

function toPage<T>(body: ApiListResponse<unknown> | null, read: (item: unknown) => T): Page<T> {
  const items = Array.isArray(body?.data) ? body.data.map(read) : [];
  return { items, paging: body?.paging ?? singlePage(items.length) };
}

function toCustomer(item: unknown): OrganizationCustomer {
  const source = isRecord(item) ? item : {};
  return {
    id: idString(source['id']),
    name: textOrNull(source, 'name'),
    address: textOrNull(source, 'address'),
    city: textOrNull(source, 'city'),
    state: textOrNull(source, 'state'),
    zip: textOrNull(source, 'zip'),
    phone: textOrNull(source, 'phone'),
    email: textOrNull(source, 'email'),
    notes: textOrNull(source, 'notes'),
    fieldCount: countOrZero(source['fieldCount']),
  };
}

function toField(item: unknown): OrganizationCustomerField {
  const source = isRecord(item) ? item : {};
  return {
    id: idString(source['id']),
    name: textOrNull(source, 'name'),
    cropType: textOrNull(source, 'cropType'),
    acreage: nullableNumber(source['acreage']) ?? null,
    city: textOrNull(source, 'city'),
    state: textOrNull(source, 'state'),
  };
}

function toTestResult(item: unknown): OrganizationTestResult {
  const source = isRecord(item) ? item : {};
  return {
    id: idString(source['id']),
    profileId: textOrNull(source, 'profile_id'),
    testId: textOrNull(source, 'test_id'),
    firstName: textOrNull(source, 'first_name'),
    lastName: textOrNull(source, 'last_name'),
    testName: textOrNull(source, 'test_name'),
    score: nullableNumber(source['score']) ?? null,
    passed: source['passed'] === true,
    completedAt: dateText(source['completed_at']),
  };
}

function dateText(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
